import { useNavigate, useSearchParams } from 'react-router-dom';

import { PageViewedTracker } from '@zenbusiness/application-commons-clickstream';
import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Button from '@zenbusiness/zui/Button';
import PageLoading from '@zenbusiness/zui/PageLoading';

import WhatsNext, { WHATS_NEXT_STATUS } from '#client/components/WhatsNext';
import { APP_PAGE_NAMES, APP_SECTION } from '#client/constants/clickstream/failedPaymentRecovery';
import { dashboard } from '#client/constants/routes';
import useUnpaidInvoices from '#client/hooks/useUnpaidInvoices';

import InvoiceCard from './components/InvoiceCard';
import InvoicePaymentsLayout from './components/InvoicePaymentsLayout';
import PaymentOrderInfo from './components/PaymentOrderInfo';
import useBnplRecoveryContext from './hooks/useBnplRecoveryContext';
import { InvoicePaymentsEmptyState } from './InvoicePayments.style';

const MESSAGE_IDS = {
  title: 'dashboard.invoicePayments.title',
  subtitle: 'dashboard.invoicePayments.subtitle',
  noInvoices: 'dashboard.invoicePayments.noInvoices',
  invalidEntity: 'dashboard.invoicePayments.invalidEntity',
  errorTitle: 'dashboard.invoicePayments.error.title',
  errorDescription: 'dashboard.invoicePayments.error.description',
  backToDashboard: 'dashboard.invoicePayments.error.backToDashboard'
};

function InvoicePayments() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { formatDate } = useIntl();

  const accountUuid = searchParams.get('accountUuid');
  const companyName = searchParams.get('companyName') || '';

  const { unpaidInvoices, loading, error } = useUnpaidInvoices(accountUuid);
  const { dynamicProperties } = useBnplRecoveryContext({ unpaidInvoices });

  const sortedInvoices = [...unpaidInvoices].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
  );

  const backToDashboardButton = (
    <Button
      variant="contained"
      size="large"
      onClick={() => navigate(dashboard)}
      sx={{ alignSelf: { xs: 'stretch', sm: 'flex-start' } }}
    >
      <FormattedMessage id={MESSAGE_IDS.backToDashboard} />
    </Button>
  );

  if (!accountUuid) {
    return (
      <InvoicePaymentsLayout companyName={companyName} titleId={MESSAGE_IDS.title}>
        <InvoicePaymentsEmptyState>
          <FormattedMessage id={MESSAGE_IDS.invalidEntity} />
        </InvoicePaymentsEmptyState>
        <WhatsNext status={WHATS_NEXT_STATUS.UNKNOWN} />
        {backToDashboardButton}
      </InvoicePaymentsLayout>
    );
  }

  if (loading) {
    return <PageLoading />;
  }

  if (error) {
    return (
      <InvoicePaymentsLayout
        companyName={companyName}
        titleId={MESSAGE_IDS.errorTitle}
        subtitleId={MESSAGE_IDS.errorDescription}
      >
        <WhatsNext status={WHATS_NEXT_STATUS.UNKNOWN} />
        {backToDashboardButton}
      </InvoicePaymentsLayout>
    );
  }

  return (
    <InvoicePaymentsLayout
      companyName={companyName}
      titleId={MESSAGE_IDS.title}
      subtitleId={MESSAGE_IDS.subtitle}
    >
      {sortedInvoices.length > 0 ? (
        <>
          <PageViewedTracker
            appPageName={APP_PAGE_NAMES.failedPaymentRecovery}
            appSection={APP_SECTION}
            dynamicProperties={dynamicProperties}
          />
          {sortedInvoices.map((invoice, index) => (
            <InvoiceCard
              key={invoice.id}
              amount={Number(invoice.total)}
              dueDate={formatDate(invoice.createdAt, { month: 'short', day: 'numeric', year: 'numeric' })}
              dynamicProperties={index === 0 ? dynamicProperties : undefined}
              isLocked={index > 0}
            />
          ))}
          <PaymentOrderInfo />
        </>
      ) : (
        <InvoicePaymentsEmptyState>
          <FormattedMessage id={MESSAGE_IDS.noInvoices} />
        </InvoicePaymentsEmptyState>
      )}
    </InvoicePaymentsLayout>
  );
}

export default InvoicePayments;
